/* eslint-disable no-console */

import { register } from "register-service-worker";

// Keep the registration around so we can check for / apply version updates at runtime
let swRegistration = null;

function setRegistration(registration) {
  if (registration) swRegistration = registration;
}

// Is there a new version already downloaded and waiting to be activated
export function hasPendingUpdate() {
  return !!(swRegistration && swRegistration.waiting);
}

function withTimeout(promise, timeout) {
  return Promise.race([
    promise,
    new Promise(resolve => setTimeout(resolve, timeout))
  ]);
}

// Asks the server whether a new version was deployed.
// Resolves to true when a new version is downloaded and waiting to be activated.
export async function checkForUpdate({ timeout = 5000 } = {}) {
  if (!swRegistration) return false;

  try {
    await withTimeout(swRegistration.update(), timeout);
  } catch (e) {
    return hasPendingUpdate();
  }

  // The new version may still be downloading - wait for it, but never block the user for long
  const installing = swRegistration.installing;
  if (installing) {
    await new Promise(resolve => {
      const done = () => {
        installing.removeEventListener("statechange", onStateChange);
        resolve();
      };
      const onStateChange = () => {
        if (
          installing.state === "installed" ||
          installing.state === "redundant"
        )
          done();
      };
      installing.addEventListener("statechange", onStateChange);
      setTimeout(done, timeout);
    });
  }

  return hasPendingUpdate();
}

// Activates the waiting version (skipWaiting) and waits for the new SW to take control.
// Once it does, the next navigation is served with the new files.
export function activateUpdate({ timeout = 3000 } = {}) {
  if (!hasPendingUpdate()) return Promise.resolve(false);

  return new Promise(resolve => {
    let settled = false;
    const finish = result => {
      if (settled) return;
      settled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve(result);
    };
    const onChange = () => finish(true);

    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
    setTimeout(() => finish(false), timeout);
  });
}

if (process.env.NODE_ENV === "production") {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready(registration) {
      setRegistration(registration);
      console.log(
        "App is being served from cache by a service worker.\n" +
          "For more details, visit https://goo.gl/AFskqB"
      );
    },
    registered(registration) {
      setRegistration(registration);
      console.log("Service worker has been registered.");
    },
    cached(registration) {
      setRegistration(registration);
      console.log("Content has been cached for offline use.");
    },
    updatefound(registration) {
      setRegistration(registration);
      console.log("New content is downloading.");
    },
    updated(registration) {
      setRegistration(registration);
      console.log("New content is available; will be applied on next login.");
    },
    offline() {
      console.log(
        "No internet connection found. App is running in offline mode."
      );
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    }
  });
}
